// Calendars beside the content calendar: what is subscribed, and what is on them.
//
// Separate from api.ts because it is a separate concern with a separate owner:
// posts belong to a workspace and are governed by permissions, whereas a
// subscription belongs to a *person* and follows them into every workspace they
// work in. Nothing in here takes a workspace id, and that is the design rather
// than an omission — the two exceptions are the share tokens at the bottom,
// which publish one workspace's posts and say so.

import { supabase, callFunction, FUNCTIONS_URL } from "./supabase";
import { asAppError } from "./entitlements";

export type EventCategory = "public" | "observance" | "other";

export interface CalendarFeed {
  id: string;
  is_catalog: boolean;
  name: string;
  country_code: string | null;
  ics_url: string | null;
  builtin_country: string | null;
  /** 0-359. Saturation and lightness belong to the theme (ADR-0014). */
  hue: number;
  last_fetched_at: string | null;
  next_fetch_at: string;
  failure_count: number;
  /** Why the last fetch failed, so the UI can say "stale, and here is why". */
  last_error: string | null;
  event_count: number;
}

export interface CalendarSubscription {
  feed_id: string;
  is_visible: boolean;
  /** Null means "whatever the feed says". */
  hue: number | null;
  categories: EventCategory[];
  created_at: string;
}

export interface CalendarEvent {
  id: string;
  feed_id: string;
  uid: string;
  /** YYYY-MM-DD. A date, not an instant: a holiday is the same day everywhere. */
  starts_on: string;
  /** Inclusive last day. */
  ends_on: string;
  all_day: boolean;
  starts_at: string | null;
  summary: string;
  description: string | null;
  category: EventCategory;
  source: "ics" | "builtin";
}

/** The catalogue plus anything this person added or subscribed to. RLS decides. */
export async function listCalendarFeeds(): Promise<CalendarFeed[]> {
  const { data, error } = await supabase
    .from("calendar_feeds")
    .select("*")
    .eq("is_active", true)
    .order("is_catalog", { ascending: false })
    .order("name");
  if (error) throw asAppError(error);
  return (data ?? []) as CalendarFeed[];
}

export async function listCalendarSubscriptions(): Promise<CalendarSubscription[]> {
  const { data, error } = await supabase
    .from("calendar_subscriptions")
    .select("feed_id, is_visible, hue, categories, created_at");
  if (error) throw asAppError(error);
  return (data ?? []) as CalendarSubscription[];
}

/**
 * Tick a calendar on.
 *
 * `user_id` is filled from the session rather than passed: the row-level policy
 * requires it to equal `auth.uid()`, so sending anything else is a refusal
 * waiting to happen.
 */
export async function subscribeToFeed(feedId: string): Promise<void> {
  const { data: session } = await supabase.auth.getUser();
  const userId = session.user?.id;
  if (!userId) throw new Error("Sign in to change your calendars.");

  const { error } = await supabase
    .from("calendar_subscriptions")
    .upsert({ user_id: userId, feed_id: feedId, is_visible: true }, { onConflict: "user_id,feed_id" });
  if (error) throw asAppError(error);
}

/** Untick it. The row goes, so an unused calendar leaves nothing behind. */
export async function unsubscribeFromFeed(feedId: string): Promise<void> {
  const { error } = await supabase.from("calendar_subscriptions").delete().eq("feed_id", feedId);
  if (error) throw asAppError(error);
}

/** Hide it for now, or change its colour or which kinds of day it shows. */
export async function updateSubscription(
  feedId: string,
  patch: Partial<Pick<CalendarSubscription, "is_visible" | "hue" | "categories">>,
): Promise<void> {
  const { error } = await supabase
    .from("calendar_subscriptions").update(patch).eq("feed_id", feedId);
  if (error) throw asAppError(error);
}

/**
 * Add any calendar by URL — a client's campaign calendar, the team's shared one.
 *
 * `webcal://` is normalised in the database rather than here, because the phone
 * that produced the link is not the only thing that will ever paste one.
 */
export async function subscribeByUrl(url: string, name?: string): Promise<CalendarFeed> {
  const { data, error } = await supabase.rpc("subscribe_calendar_url", {
    p_url: url.trim(),
    p_name: name?.trim() || null,
  });
  if (error) throw asAppError(error);
  return data as CalendarFeed;
}

/**
 * Fetch a feed now rather than waiting for the nightly sweep.
 *
 * The Edge Function does the fetching because the published holiday calendars
 * send no CORS header — a browser cannot read them at all, so this is not a
 * convenience wrapper around something the page could do itself.
 */
export async function refreshFeed(feedId: string): Promise<{ refreshed: number; failed: number }> {
  return callFunction("calendar-refresh", { feed_id: feedId });
}

/**
 * Everything landing in a window, from the calendars this person subscribes to.
 *
 * Reads the `_effective` view rather than the table: a catalogue feed carries
 * both a fetched copy and the offline base, and the view picks one per year so
 * Christmas never appears twice.
 */
export async function listCalendarEvents(
  feedIds: string[],
  from: Date,
  to: Date,
): Promise<CalendarEvent[]> {
  if (feedIds.length === 0) return [];
  const day = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  const { data, error } = await supabase
    .from("calendar_events_effective")
    .select("*")
    .in("feed_id", feedIds)
    // Overlap, not containment: a multi-day festival that began before this
    // month still belongs on the first row of it.
    .lte("starts_on", day(to))
    .gte("ends_on", day(from))
    .order("starts_on")
    .limit(1000);
  if (error) throw asAppError(error);
  return (data ?? []) as CalendarEvent[];
}

/**
 * Which calendars a first-time visitor probably wants, from the browser's own
 * locale and timezone.
 *
 * A guess, offered and never applied: nothing is subscribed on somebody's behalf.
 * Getting this wrong for a Delhi-based agency running a US launch is exactly why
 * it is a suggestion in the picker rather than a default in the database.
 */
export function likelyCountries(): string[] {
  const out = new Set<string>();
  try {
    for (const tag of navigator.languages ?? [navigator.language]) {
      const region = new Intl.Locale(tag).maximize().region;
      if (region) out.add(region);
    }
  } catch {
    // An engine without Intl.Locale gets no guess, which costs one extra click.
  }
  return [...out];
}

// ---------------------------------------------------------------- sharing out

export interface CalendarShare {
  id: string;
  workspace_id: string;
  label: string;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

export async function listCalendarShares(workspaceId: string): Promise<CalendarShare[]> {
  const { data, error } = await supabase
    .from("calendar_share_tokens")
    .select("id, workspace_id, label, created_at, last_used_at, revoked_at")
    .eq("workspace_id", workspaceId)
    .is("revoked_at", null)
    .order("created_at", { ascending: false });
  if (error) throw asAppError(error);
  return (data ?? []) as CalendarShare[];
}

/**
 * Mint a subscription link. The token comes back exactly once — the database
 * keeps only its hash — which is why the caller must show it immediately and
 * why there is a label: one link per device beats one link re-issued.
 */
export async function createCalendarShare(
  workspaceId: string,
  label: string,
): Promise<{ id: string; token: string; label: string; url: string; webcal: string }> {
  const { data, error } = await supabase.rpc("create_calendar_share", {
    p_workspace_id: workspaceId,
    p_label: label,
  });
  if (error) throw asAppError(error);

  const row = (Array.isArray(data) ? data[0] : data) as { id: string; token: string; label: string };
  const url = `${FUNCTIONS_URL}/calendar-export?t=${row.token}`;
  return {
    ...row,
    url,
    // The same URL with the scheme that tells an operating system "this is a
    // subscription": clicking it opens Apple Calendar or Outlook directly
    // instead of downloading a file that goes stale the moment it lands.
    webcal: url.replace(/^https:/, "webcal:"),
  };
}

export async function revokeCalendarShare(id: string): Promise<void> {
  const { error } = await supabase.rpc("revoke_calendar_share", { p_id: id });
  if (error) throw asAppError(error);
}
